import React, { useEffect } from 'react';
import { Card } from '@material-ui/core';
import { Subject as SubjectIcon } from '@material-ui/icons';

import CardTitle from '../../../common/card/CardTitle';
import CardContent from '../../../common/card/CardContent';
import InlineForm from '../../../common/inline/InlineForm';
import InlineFormik from '../../../common/inline/InlineFormik';
import InlineTextField from '../../../common/inline/InlineTextField';
import { BaseSupplierCardProps, Supplier } from '../Supplier.interface';
import { useTaskEntityFields } from '../../workflow/Workflow.hooks';
import { useUpdateSupplier } from '../Supplier.hooks';
import InlineNumberField from '../../../common/inline/InlineNumberField';

const SupplierDetailsCard: React.FC<BaseSupplierCardProps> = (
  props: BaseSupplierCardProps
) => {
  const {
    supplier,
    supplierVersion,
    token,
    onChange,
    onUpdate,
    onError
  } = props;
  const {
    updatedSupplier,
    isUpdating,
    updateError,
    updateSupplier
  } = useUpdateSupplier(supplier.id, token);
  const { isFieldDisabled } = useTaskEntityFields(supplier.task, { token });
  const disabled = !!supplierVersion;
  const initialData = supplierVersion || supplier;

  useEffect(() => {
    if (updatedSupplier) {
      onChange?.(updatedSupplier);
      onUpdate?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updatedSupplier]);

  useEffect(() => {
    if (updateError) {
      onError?.(updateError);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateError]);

  return (
    <Card>
      <CardTitle>
        <SubjectIcon />
        Details
      </CardTitle>
      <CardContent padding='large'>
        <InlineFormik
          isSubmitting={isUpdating}
          initialValues={{
            companyName: initialData.companyName || '',
            contactName: initialData.contactName || '',
            phoneNumber: initialData.phoneNumber || '',
            address: initialData.address || '',
            numberOfLocations: initialData.numberOfLocations || 0,
            primaryLocation: initialData.primaryLocation || '',
            numberOfEmployees: initialData.numberOfEmployees || 0,
            productServices: initialData.productServices || ''
          }}
          onSubmit={(supplierDto: Partial<Supplier>) => {
            updateSupplier(supplierDto);
            onChange?.(supplierDto);
          }}
        >
          {() => (
            <InlineForm>
              <InlineTextField
                disabled={disabled || isFieldDisabled('companyName')}
                name='companyName'
                label='Company Name'
                placeholder='Enter Company Name'
              />
              <InlineTextField
                disabled={disabled || isFieldDisabled('contactName')}
                name='contactName'
                label='Name of the person filling out this form'
                placeholder='Enter Person Name'
              />
              <InlineNumberField
                phoneNumber
                disabled={disabled || isFieldDisabled('phoneNumber')}
                name='phoneNumber'
                label='Phone Number'
                placeholder='Enter Phone Number'
              />
              <InlineTextField
                multiline
                disabled={disabled || isFieldDisabled('address')}
                name='address'
                label='Address'
                placeholder='Enter Address'
              />
              <InlineTextField
                type='number'
                disabled={disabled || isFieldDisabled('numberOfLocations')}
                name='numberOfLocations'
                label='Number of Locations'
                placeholder='Enter Number of Locations'
              />
              <InlineTextField
                multiline
                disabled={disabled || isFieldDisabled('primaryLocation')}
                name='primaryLocation'
                label='Primary Location/Headquarters'
                placeholder='Enter Primary Location'
              />
              <InlineTextField
                type='number'
                disabled={disabled || isFieldDisabled('numberOfEmployees')}
                name='numberOfEmployees'
                label='Number of Employees'
                placeholder='Enter Number of Employees'
              />
              <InlineTextField
                multiline
                disabled={disabled || isFieldDisabled('productServices')}
                name='productServices'
                label='Products/Services Offered'
                placeholder='Enter Products/Services Offered'
              />
            </InlineForm>
          )}
        </InlineFormik>
      </CardContent>
    </Card>
  );
};

export default SupplierDetailsCard;
